import { z } from "zod";
import type { IconProps } from "@/ui/icon/Icon";
import { TranslationSchema } from "@/utils/i18n";

export namespace Calc {
  export namespace Profile {
    export const ProfileSchema = z.object({
      id: z.number(),
      value: z.string(),
      title: z.string(),
    });

    export type Profile = z.infer<typeof ProfileSchema>;
  }

  export namespace Form {
    export const CounterSchema = z.object({
      id: z.string(),
      value: z.number(),
      defaultValue: z.number(),
      min: z.number(),
      max: z.number(),
    });

    export type Counter = z.infer<typeof CounterSchema>;

    export const SelectSchema = z.object({
      id: z.string(),
      icon: z.string() as unknown as z.ZodLiteral<IconProps["name"]>,
      value: z.number(),
      defaultValue: z.number(),
      options: Profile.ProfileSchema.array(),
    });

    export type Select = z.infer<typeof SelectSchema>;
  }

  export namespace Price {
    const LabelSchema = z.object({
      type: z.enum(["text", "accent"]),
      text: z.string(),
    });

    export type Label = z.infer<typeof LabelSchema>;

    export const PriceSchema = z.object({
      currency: z.string(),
      value: z.number(),
      label: LabelSchema.array().optional(),
      period: z.string().optional(),
      discount: z.number().optional(),
    });

    export type Price = z.infer<typeof PriceSchema>;
  }

  export namespace Module {
    const ModuleBaseSchema = z.object({
      id: z.string(),
      active: z.boolean(),
      isSubscribed: z.boolean(),
      title: z.string(),
      text: z.string(),
      price: Price.PriceSchema,
    });

    const ModuleSimpleSchema = ModuleBaseSchema.extend({
      type: z.literal("simple"),
    });

    const ModuleWithCounterSchema = ModuleBaseSchema.extend({
      type: z.literal("counter"),
      counter: Form.CounterSchema,
    });

    const ModuleWithSelectSchema = ModuleBaseSchema.extend({
      type: z.literal("select"),
      select: Form.SelectSchema.array(),
    });

    export const ModuleSchema = z.union([
      ModuleSimpleSchema,
      ModuleWithCounterSchema,
      ModuleWithSelectSchema,
    ]);

    export type Module = z.infer<typeof ModuleSchema>;
  }

  export namespace Pack {
    const ModuleSchema = z.object({
      active: z.boolean(),
      title: z.string(),
      value: z.union([z.string(), z.number()]).optional(),
    });

    export type Module = z.infer<typeof ModuleSchema>;

    const IncludedModuleSchema = z.object({
      type: z.literal("default"),
      value: z.string().optional(),
      items: z
        .object({
          title: z.string(),
          tooltip: z.string().optional(),
        })
        .array(),
    });

    export type IncludedModule = z.infer<typeof IncludedModuleSchema>;

    const IncludedConfigurableModuleSchema = z.object({
      id: z.string(),
      type: z.literal("select"),
      select: Form.SelectSchema,
      items: z
        .object({
          title: z.string(),
          tooltip: z.string().optional(),
        })
        .array(),
    });

    export type IncludedConfigurableModule = z.infer<
      typeof IncludedConfigurableModuleSchema
    >;

    export const PackSchema = z.object({
      id: z.string(),
      type: z.enum(["default", "module"]),
      color: z.enum(["default", "popular"]),
      title: z.string(),
      price: Price.PriceSchema,
      badge: z.string().optional(),
      info: z.string().optional(),
      modules: ModuleSchema.array(),
      includedModules: z
        .array(
          z.union([IncludedModuleSchema, IncludedConfigurableModuleSchema]),
        )
        .optional(),
    });

    export type Pack = z.infer<typeof PackSchema>;
  }

  export namespace Period {
    export const PeriodSchema = z.object({
      id: z.string(),
      title: z.string(),
      label: z.string().optional(),
      discount: z.number().optional(),
    });

    export type Period = z.infer<typeof PeriodSchema>;
  }

  export namespace Content {
    const TextPartSchema = z.object({
      type: z.enum(["text", "bold", "green", "grey"]),
      item: z.string(),
    });

    export type TextPart = z.infer<typeof TextPartSchema>;

    export const MessagePartSchema = z.object({
      type: z.literal("message"),
      item: TextPartSchema.array(),
    });

    export type MessagePart = z.infer<typeof MessagePartSchema>;

    const TrialPartSchema = z.object({
      type: z.literal("trial"),
    });

    export type TrialPart = z.infer<typeof TrialPartSchema>;

    const PromoCodePartSchema = z.object({
      type: z.literal("promo-code"),
    });

    export type PromoCodePart = z.infer<typeof PromoCodePartSchema>;

    const TitlePartSchema = z.object({
      type: z.literal("title"),
      item: z.string(),
    });

    const PricePartSchema = z.object({
      type: z.literal("price"),
      item: Price.PriceSchema,
    });

    const InfoPartSchema = z.object({
      type: z.literal("info"),
      item: z
        .union([MessagePartSchema, TrialPartSchema, PromoCodePartSchema])
        .array(),
    });

    export type InfoPart = z.infer<typeof InfoPartSchema>;

    export const PartSchema = z.union([
      TitlePartSchema,
      PricePartSchema,
      InfoPartSchema,
    ]);

    export type Part = z.infer<typeof PartSchema>;
  }

  export namespace Calculator {
    const PaymentTypeSchema = z.enum(["card", "paypal", "crypto"]);

    export type PaymentType = z.infer<typeof PaymentTypeSchema>;

    export const CalculatorSchema = z.object({
      type: z.enum(["default", "custom-pack"]),
      isAuthorized: z.boolean(),
      isCustomActive: z.boolean(),
      isTrialActive: z.boolean(),
      isRequestDemoActive: z.boolean(),
      promoCode: z.string().optional(),
      activePack: z.string(),
      packs: Pack.PackSchema.array(),
      modules: Module.ModuleSchema.array(),
      activePeriod: z.string(),
      periods: Period.PeriodSchema.array(),
      content: Content.PartSchema.array(),
      paymentTypes: PaymentTypeSchema.array(),
    });

    export type Calculator = z.infer<typeof CalculatorSchema>;
  }

  export namespace Payment {
    const TypeSchema = z.literal([
      "card",
      "apple-pay",
      "google-pay",
      "pix",
      "paypal",
      "crypto",
    ]);

    export type Type = z.infer<typeof TypeSchema>;

    export const MethodSchema = z.object({
      id: z.number().int(),
      type: TypeSchema,
      active: z.boolean(),
      title: z.string().min(1),
      text: z.string().optional(),
    });

    export type Method = z.infer<typeof MethodSchema>;
  }

  export namespace Links {
    export const TermsSchema = z.object({
      privacyPolicy: z.string(),
      termsOfService: z.string(),
    });

    export type Terms = z.infer<typeof TermsSchema>;

    export const LinksSchema = z.object({
      terms: TermsSchema,
    });

    export type Links = z.infer<typeof LinksSchema>;
  }

  export namespace Api {
    export const EndpointsSchema = z.object({
      calculatorUpdate: z.string(),
      validatePromoCode: z.string(),
      getPaymentMethods: z.string(),
      paymentSubmit: z.string(),
    });

    export type Endpoints = z.infer<typeof EndpointsSchema>;

    export const CalculatorUpdateRequestSchema = z.object({
      isCustomActive: z.boolean(),
      isTrialActive: z.boolean(),
      promoCode: z.string().optional(),
      activePack: z.string(),
      activePeriod: z.string(),
      modules: z.record(
        z.string(),
        z.object({
          active: z.boolean(),
          values: z.record(z.string(), z.number()),
        }),
      ),
    });

    export type CalculatorUpdateRequest = z.infer<
      typeof CalculatorUpdateRequestSchema
    >;

    export const CalculatorUpdateResponseSchema = z.object({
      isAuthorized: z.boolean(),
      packs: z
        .object({
          id: z.string(),
          price: Price.PriceSchema,
          info: z.string().optional(),
        })
        .array(),
      modules: z
        .object({
          id: z.string(),
          price: Price.PriceSchema,
        })
        .array(),
      content: Content.PartSchema.array(),
    });

    export type CalculatorUpdateResponse = z.infer<
      typeof CalculatorUpdateResponseSchema
    >;

    export const PaymentMethodsRequestSchema = CalculatorUpdateRequestSchema;

    export type PaymentMethodsRequest = z.infer<
      typeof PaymentMethodsRequestSchema
    >;

    const PaymentMethodTypeSchema = z.enum([
      "card",
      "apple-pay",
      "google-pay",
      "pix",
      "paypal",
      "crypto",
    ]);

    export const PaymentMethodSchema = z.object({
      id: z.number(),
      type: PaymentMethodTypeSchema,
      active: z.boolean(),
      title: z.string(),
      text: z.string().optional(),
    });

    export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

    export const PaymentMethodsResponseSchema = z.object({
      info: Content.MessagePartSchema.array(),
      paymentMethods: PaymentMethodSchema.array(),
    });

    export type PaymentMethodsResponse = z.infer<
      typeof PaymentMethodsResponseSchema
    >;

    export const PaymentSubmitRequestSchema =
      CalculatorUpdateRequestSchema.extend({
        paymentMethod: z.number().optional(),
        paymentMethodType: PaymentMethodTypeSchema.optional(),
      });

    export type PaymentSubmitRequest = z.infer<
      typeof PaymentSubmitRequestSchema
    >;

    export const PaymentSubmitResponseSchema = z.union([
      z.object({
        redirectType: z.literal("url"),
        redirectUrl: z.string(),
      }),
      z.object({
        redirectType: z.literal("modal"),
        redirectModal: z.string(),
      }),
    ]);

    export type PaymentSubmitResponse = z.infer<
      typeof PaymentSubmitResponseSchema
    >;

    export const PromoCodeValidationRequestSchema =
      CalculatorUpdateRequestSchema.extend({
        promoCode: z.string(),
      });

    export type PromoCodeValidationRequest = z.infer<
      typeof PromoCodeValidationRequestSchema
    >;

    export const PromoCodeValidationResponseSchema = z.object({
      error: z.string().optional(),
      text: z.string().optional(),
    });

    export type PromoCodeValidationResponse = z.infer<
      typeof PromoCodeValidationResponseSchema
    >;
  }

  export const ConfigSchema = z.object({
    api: Api.EndpointsSchema,
    links: Links.LinksSchema,
    calculators: z.record(z.string(), Calculator.CalculatorSchema),
    translation: TranslationSchema,
  });

  export type Config = z.infer<typeof ConfigSchema>;
}
